'use client'

import Link from 'next/link'
import { css, styled } from 'styled-components'

import { FormattedMessage } from '@b-stock/fe-core/bstock-react'
import { Colors, Typography } from '@b-stock/fe-core/design-system'

import {
  STOREFRONT_ACCESS_GATE_CONTENT,
  type StorefrontAccessGateContent,
} from './storefrontAccessGateContent'

const framedStyles = css`
  box-sizing: border-box;
  /* The side gutter keeps the card off the screen edges on Mobile web. */
  width: calc(100% - 2rem);
  max-width: 38.75rem;
  margin: 2.5rem auto;
  border: 0.0625rem solid ${Colors.Semantic.Neutral.medium_grey};
  border-radius: 0.625rem;
  background: ${Colors.Semantic.Neutral.white};
`

const Gate = styled.section<{ $framed: boolean }>`
  padding: 1.75rem 1.875rem;
  ${(props) => props.$framed && framedStyles}
`

const Heading = styled.h1`
  ${Typography.Title3}
  margin: 0 0 0.5rem;
`

const Body = styled.p`
  ${Typography.Body2}
  color: ${Colors.Semantic.Neutral.dark_grey};
  margin: 0.5rem 0;
`

const KeyBuyerLine = styled.p`
  ${Typography.Body3}
  margin: 0.875rem 0 0;
  padding: 0.625rem 0.75rem;
  border-radius: 0.5rem;
  background: ${Colors.Semantic.Info.background};
`

/**
 * What a buyer the AT&T access decision denies sees instead of the storefront or a listing: the
 * heading, the body and the Key Buyer line, all from one content config. No apply action and no
 * badge; every denied buyer sees the same gate, whatever the reason. `framed` draws the gate's own
 * card; pass false when a parent card already frames it.
 */
export default function StorefrontAccessGate({
  content = STOREFRONT_ACCESS_GATE_CONTENT,
  framed = true,
}: {
  content?: StorefrontAccessGateContent
  framed?: boolean
}) {
  const { linkUrl, textId } = content.keyBuyerLine
  const keyBuyerText = <FormattedMessage id={textId} />

  return (
    <Gate $framed={framed} data-testid="storefront-access-denied">
      <Heading>
        <FormattedMessage id={content.headingId} />
      </Heading>
      <Body>
        <FormattedMessage id={content.bodyId} />
      </Body>
      <KeyBuyerLine data-testid="storefront-access-key-buyer-line">
        {linkUrl ? <Link href={linkUrl}>{keyBuyerText}</Link> : keyBuyerText}
      </KeyBuyerLine>
    </Gate>
  )
}
